'use client';

import { useEffect, useState } from 'react';
import { Clock3 } from 'lucide-react';
import { useBrowserOnline } from '@workspace/ui/hooks/use-browser-online';
import { cn } from '@workspace/ui/lib/utils';

// Mounted only while the operation is pending, so presence is the pending signal.
// Floats above the surface by default: never inserts into the loading layout, so a
// delayed wait cannot shift skeletons or spinners.
interface DelayedOperationNoticeProps {
  delayMs: number;
  message?: string;
  className?: string;
}

export function DelayedOperationNotice({
  delayMs,
  message = 'Slow internet detected. Please check your connection.',
  className,
}: DelayedOperationNoticeProps) {
  const online = useBrowserOnline();
  const [delayed, setDelayed] = useState(false);

  useEffect(() => {
    const restart = () => {
      clearTimeout(timer);
      setDelayed(false);
      if (online && document.visibilityState !== 'hidden') {
        timer = setTimeout(() => setDelayed(true), delayMs);
      }
    };
    let timer: ReturnType<typeof setTimeout> | undefined;
    restart();
    document.addEventListener('visibilitychange', restart);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', restart);
    };
  }, [delayMs, online]);

  if (!delayed || !online) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={cn(
        'pointer-events-none fixed inset-x-4 top-[calc(var(--header-height,64px)+0.75rem)] z-40 flex justify-center animate-fade-in print:hidden',
        className,
      )}
    >
      <span className="inline-flex max-w-full items-center gap-2 rounded-2xl sm:rounded-full border border-purple-30 bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-primary-darker animate-glow-breath-primary">
        <Clock3 className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <span className="min-w-0 break-words">{message}</span>
      </span>
    </div>
  );
}
